import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { toast } from 'react-toastify';
import { Landmark, RefreshCw, AlertTriangle, CheckCircle2, ArrowRightLeft, X } from 'lucide-react';

/**
 * Which rail rider withdrawals go out on — Paystack or Flutterwave.
 *
 * A manual switch, on purpose. The failure mode it exists for is "Paystack is
 * delaying transfers this morning"; the person seeing that should be the one
 * who flips it, and should see both balances before doing so.
 *
 * Switching creates one piece of follow-up work: the two providers use different
 * codes for the fintech banks, so every rider's saved bank has to be re-resolved
 * under the new rail. That happens on its own at each rider's next withdrawal,
 * or all at once from here.
 */

const money = (v: any) => `₦${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const apiError = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

function PayoutProviderSwitch() {
    // Gated on its own permission; admin and super_admin always see it. The
    // server enforces the same rule — this only keeps the card, and a 403 toast,
    // off the screens of staff who cannot use it.
    const userLoginData = useSelector((s: any) => s.data.login.value);
    const allowed = useMemo(() => {
        const role = userLoginData?.data?.role;
        if (role === 'admin' || role === 'super_admin') return true;
        try {
            const perms = userLoginData?.data?.permissions ? JSON.parse(userLoginData.data.permissions) : [];
            return Array.isArray(perms) && perms.includes('can_manage_payout_provider');
        } catch {
            return false;
        }
    }, [userLoginData]);

    const [state, setState] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [switching, setSwitching] = useState<string>('');

    // Bulk re-verification runs in batches; this is its live tally.
    const [reverifying, setReverifying] = useState<boolean>(false);
    const [tally, setTally] = useState<{ verified: number; failed: any[]; remaining: number | null } | null>(null);
    // Both lists from the server: not yet checked, and checked-but-failed.
    const [failures, setFailures] = useState<{ pending: any[]; failed: any[] } | null>(null);

    // The two confirmations, as real modals rather than browser dialogs.
    const [confirmSwitch, setConfirmSwitch] = useState<any>(null);   // the provider being switched to
    const [switchNote, setSwitchNote] = useState<string>('');
    const [confirmReverify, setConfirmReverify] = useState<boolean | 'failed'>(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await SuperAdminApis.getPayoutProvider();
            if (res?.status) setState(res.data);
            else toast.warn(res?.message || 'Could not load the payout provider');
        } catch (e) {
            toast.error(apiError(e, 'Could not load the payout provider'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (allowed) load(); }, [allowed, load]);

    const current = state?.providers?.find((p: any) => p.active);
    const other   = state?.providers?.find((p: any) => !p.active);

    // Riders whose bank is verified under a rail other than the current one.
    const needsReverify = state && current
        ? (state.banks_pending ?? Math.max(0, state.banks_total - current.banks_verified))
        : 0;
    const failedCount = state?.banks_failed ?? 0;

    const switchTo = (p: any) => {
        if (!p.configured) {
            toast.warn(`${p.label} is not configured on the server — its secret key is missing.`);
            return;
        }
        setSwitchNote('');
        setConfirmSwitch(p);
    };

    const doSwitch = async () => {
        const p = confirmSwitch;
        if (!p) return;

        setSwitching(p.name);
        try {
            const res = await SuperAdminApis.switchPayoutProvider({ provider: p.name, note: switchNote.trim() || undefined });
            if (res?.status) {
                toast.success(res.message, { autoClose: 10000 });
                setConfirmSwitch(null);
                setTally(null);
                setFailures(null);
                load();
            } else {
                toast.warn(res?.message || 'Could not switch', { autoClose: 10000 });
            }
        } catch (e) {
            toast.error(apiError(e, 'Could not switch'), { autoClose: 10000 });
        } finally {
            setSwitching('');
        }
    };

    /**
     * Batches of 20 until the server says nothing is left. Each rider costs the
     * provider a call or two, so one request cannot do hundreds.
     */
    const reverifyAll = (retryFailed = false) => setConfirmReverify(retryFailed ? 'failed' : true);

    const doReverify = async () => {
        const retryFailed = confirmReverify === 'failed';
        setConfirmReverify(false);
        setReverifying(true);
        setTally({ verified: 0, failed: [], remaining: null });

        try {
            let guard = 0;
            while (guard++ < 200) {
                const res = await SuperAdminApis.reverifyPayoutBanks(20, retryFailed);
                if (!res?.status) { toast.warn(res?.message || 'Re-verification stopped'); break; }

                const d = res.data;
                setTally((t) => ({
                    verified: (t?.verified || 0) + d.verified,
                    failed: [...(t?.failed || []), ...d.failed],
                    remaining: d.remaining,
                }));

                if (d.remaining === 0 || d.processed === 0) break;
            }
            load();
            if (failures) showFailures();
        } catch (e) {
            toast.error(apiError(e, 'Re-verification failed part-way — it can be run again'));
        } finally {
            setReverifying(false);
        }
    };

    const showFailures = async () => {
        try {
            const res = await SuperAdminApis.getPayoutBankFailures();
            if (res?.status) setFailures({ pending: res.data?.pending || [], failed: res.data?.failed || [] });
        } catch (e) {
            toast.error(apiError(e, 'Could not load the list'));
        }
    };

    if (!allowed) return null;

    if (loading && !state) {
        return <div className="bg-white rounded-lg p-4 shadow-sm mb-5 text-sm text-gray-400">Loading payout provider…</div>;
    }

    if (!state) return null;

    return (
        <div className="bg-white rounded-lg p-4 shadow-sm mb-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                    <p className="text-sm font-semibold text-[#1C1C1C] flex items-center gap-2">
                        <ArrowRightLeft size={16} className="text-[#E04343]" />
                        Payment provider for withdrawals
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Every rider and fleet withdrawal — and every retry — goes out through the active provider.
                        {state.switched_at && (
                            <> Last switched {new Date(state.switched_at).toLocaleString()}{state.switched_by ? ` by ${state.switched_by}` : ''}
                                {state.note ? ` — “${state.note}”` : ''}.</>
                        )}
                    </p>
                </div>
                <div className="text-right">
                    <button onClick={load} className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1 ml-auto">
                        <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh balances
                    </button>
                    {state.server_ip && (
                        <p className="text-[11px] text-gray-500 mt-1" title="Flutterwave only accepts transfers from whitelisted IPs">
                            Server IP to whitelist: <code className="bg-gray-100 px-1 rounded">{state.server_ip}</code>
                        </p>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {state.providers.map((p: any) => (
                    <div key={p.name}
                        className={`border rounded-lg p-4 ${p.active ? 'border-green-500 bg-green-50/40' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <p className="font-semibold text-sm text-gray-800 flex items-center gap-2">
                                <Landmark size={15} className={p.active ? 'text-green-600' : 'text-gray-400'} /> {p.label}
                                {p.active && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600 text-white">ACTIVE</span>
                                )}
                                {!p.configured && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">not configured</span>
                                )}
                            </p>
                            {!p.active && (
                                <button onClick={() => switchTo(p)} disabled={!!switching || !p.configured}
                                    className="bg-gray-800 text-white px-3 py-1.5 rounded text-xs disabled:opacity-50">
                                    {switching === p.name ? 'Switching…' : `Switch to ${p.label}`}
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <span className="text-gray-500">Balance</span>
                            <span className="font-semibold text-gray-800">
                                {p.balance !== null ? money(p.balance)
                                    : <span className="text-amber-600 font-normal" title={p.balance_error || ''}>unavailable</span>}
                            </span>
                            <span className="text-gray-500">Rider banks verified here</span>
                            <span className="text-gray-800">{p.banks_verified} of {state.banks_total}</span>
                            <span className="text-gray-500">Fee per transfer</span>
                            <span className="text-gray-800">
                                {p.fee_tiers.map((t: any, i: number) => (
                                    <span key={i}>{i > 0 && ' · '}{t.up_to ? `≤${(t.up_to / 1000)}k` : '>50k'} {money(t.fee)}</span>
                                ))}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Follow-up work the switch created. */}
            {current && needsReverify > 0 && (
                <div className="mt-3 bg-amber-50 border-l-4 border-amber-500 rounded-r px-4 py-3">
                    <p className="text-sm text-amber-900 font-semibold flex items-center gap-2">
                        <AlertTriangle size={15} /> {needsReverify} rider bank account(s) are not yet verified under {current.label}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                        {current.label} uses different codes for some banks (OPay, PalmPay, Moniepoint and the like), so
                        each account is looked up again before money is sent. This happens by itself at each rider's next
                        withdrawal — or run it for everyone now, so any that cannot be matched surface as a list instead of
                        as failed withdrawals.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                        <button onClick={() => reverifyAll(false)} disabled={reverifying}
                            className="bg-amber-600 text-white px-3 py-1.5 rounded text-xs disabled:opacity-60 flex items-center gap-1.5">
                            <RefreshCw size={12} className={reverifying ? 'animate-spin' : ''} />
                            {reverifying ? 'Re-verifying…' : `Re-verify ${needsReverify} bank account(s) now`}
                        </button>
                        {tally && (
                            <span className="text-xs text-amber-900">
                                {tally.verified} verified · {tally.failed.length} could not be matched
                                {tally.remaining !== null && tally.remaining > 0 && ` · ${tally.remaining} to go`}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {current && failedCount > 0 && (
                <p className="mt-3 text-xs text-[#E04343] flex items-center gap-1.5">
                    <AlertTriangle size={13} /> {failedCount} rider bank account(s) could not be matched under {current.label} —
                    those riders need to re-add their bank. See the list below.
                    <button onClick={() => reverifyAll(true)} disabled={reverifying}
                        className="underline hover:no-underline disabled:opacity-50">
                        Try those {failedCount} again
                    </button>
                </p>
            )}

            {current && needsReverify === 0 && failedCount === 0 && state.banks_total > 0 && (
                <p className="mt-3 text-xs text-green-700 flex items-center gap-1.5">
                    <CheckCircle2 size={13} /> Every rider bank account is verified under {current.label}.
                </p>
            )}

            {(tally?.failed.length || 0) > 0 && (
                <div className="mt-3 border rounded-lg overflow-x-auto">
                    <p className="text-xs font-semibold text-gray-700 px-3 py-2 bg-gray-50 border-b">
                        Could not be matched under {current?.label} — these riders need to re-add their bank account
                    </p>
                    <table className="w-full text-xs">
                        <tbody className="divide-y">
                            {tally!.failed.map((f: any, i: number) => (
                                <tr key={i}>
                                    <td className="px-3 py-1.5 font-medium">{f.rider || '—'}<span className="block text-gray-400">{f.email}</span></td>
                                    <td className="px-3 py-1.5">{f.bank_name} · {f.account_number}</td>
                                    <td className="px-3 py-1.5 text-amber-700">{f.error}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-2">
                <button onClick={() => (failures ? setFailures(null) : showFailures())}
                    className="text-[11px] text-gray-500 hover:text-gray-800 underline">
                    {failures ? 'Hide the list' : `Show which rider bank accounts are affected under ${current?.label || 'the current provider'}`}
                </button>

                {failures && (
                    <div className="mt-2 space-y-3">
                        {[
                            {
                                key: 'pending',
                                rows: failures.pending,
                                title: `Not checked yet (${failures.pending.length})`,
                                hint: 'Saved under another provider. Re-verify checks these; each is also checked automatically on the rider\u2019s next withdrawal.',
                                tone: 'text-amber-800',
                            },
                            {
                                key: 'failed',
                                rows: failures.failed,
                                title: `Checked and could not be matched (${failures.failed.length})`,
                                hint: 'These riders need to re-add their bank account before they can withdraw.',
                                tone: 'text-[#E04343]',
                            },
                        ].map((g) => (
                            <div key={g.key}>
                                <p className={`text-xs font-semibold ${g.tone}`}>{g.title}</p>
                                <p className="text-[11px] text-gray-500 mb-1">{g.hint}</p>
                                {g.rows.length === 0 ? (
                                    <p className="text-xs text-gray-400">None.</p>
                                ) : (
                                    <div className="border rounded-lg overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-gray-50 text-left text-gray-500"><tr>
                                                <th className="px-3 py-1.5">Rider</th>
                                                <th className="px-3 py-1.5">Phone</th>
                                                <th className="px-3 py-1.5">Bank on file</th>
                                                <th className="px-3 py-1.5">Verified under</th>
                                            </tr></thead>
                                            <tbody className="divide-y">
                                                {g.rows.map((f: any, i: number) => (
                                                    <tr key={i}>
                                                        <td className="px-3 py-1.5">{f.rider || '—'}<span className="block text-gray-400">{f.email}</span></td>
                                                        <td className="px-3 py-1.5">{f.phone || '—'}</td>
                                                        <td className="px-3 py-1.5">{f.bank_name} · {f.account_number} · {f.account_name}</td>
                                                        <td className="px-3 py-1.5 capitalize">{f.verified_under}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Switch confirmation ─────────────────────────── */}
            {confirmSwitch && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <h3 className="font-semibold flex items-center gap-2">
                                <ArrowRightLeft size={16} className="text-[#E04343]" />
                                Switch withdrawals to {confirmSwitch.label}?
                            </h3>
                            <button onClick={() => setConfirmSwitch(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>

                        <div className="px-5 py-4 space-y-4">
                            <p className="text-sm text-gray-700">
                                Every new withdrawal and every retry goes out through{' '}
                                <strong>{confirmSwitch.label}</strong> from this moment. Transfers already in
                                flight finish on the rail that sent them.
                            </p>

                            <div className="border rounded-lg divide-y text-sm">
                                <div className="flex items-center justify-between px-4 py-2.5">
                                    <span className="text-gray-500">{confirmSwitch.label} balance</span>
                                    <strong className={confirmSwitch.balance !== null && confirmSwitch.balance <= 0 ? 'text-[#E04343]' : 'text-gray-900'}>
                                        {confirmSwitch.balance !== null ? money(confirmSwitch.balance) : 'unavailable'}
                                    </strong>
                                </div>
                                <div className="flex items-center justify-between px-4 py-2.5">
                                    <span className="text-gray-500">Rider banks verified under {confirmSwitch.label}</span>
                                    <strong className="text-gray-900">{confirmSwitch.banks_verified} of {state.banks_total}</strong>
                                </div>
                                <div className="flex items-center justify-between px-4 py-2.5">
                                    <span className="text-gray-500">Fee per transfer</span>
                                    <span className="text-gray-900">
                                        {confirmSwitch.fee_tiers.map((t: any, i: number) => (
                                            <span key={i}>{i > 0 && ' · '}{t.up_to ? `≤${(t.up_to / 1000)}k` : '>50k'} {money(t.fee)}</span>
                                        ))}
                                    </span>
                                </div>
                            </div>

                            {confirmSwitch.balance !== null && confirmSwitch.balance <= 0 && (
                                <p className="text-sm text-[#E04343] bg-red-50 border-l-4 border-[#E04343] rounded-r px-3 py-2">
                                    {confirmSwitch.label} has no balance. Withdrawals will fail until it is funded.
                                </p>
                            )}

                            {state.banks_total - confirmSwitch.banks_verified > 0 && (
                                <p className="text-xs text-gray-500">
                                    {state.banks_total - confirmSwitch.banks_verified} rider bank account(s) are not yet verified
                                    under {confirmSwitch.label}. They are re-verified on their next withdrawal, or all at once
                                    with <strong>Re-verify banks</strong> after switching.
                                </p>
                            )}

                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Reason <span className="text-gray-400">— optional, kept in the audit log</span></label>
                                <input
                                    autoFocus
                                    value={switchNote}
                                    onChange={(e) => setSwitchNote(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !switching) doSwitch(); }}
                                    placeholder="e.g. Paystack transfers delayed this morning"
                                    className="w-full border rounded-md px-3 py-2 text-sm"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 px-5 py-3 border-t">
                            <button onClick={() => setConfirmSwitch(null)} disabled={!!switching}
                                className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm">Cancel</button>
                            <button onClick={doSwitch} disabled={!!switching}
                                className="bg-[#E04343] text-white px-4 py-2 rounded text-sm flex items-center gap-2 disabled:opacity-60">
                                {switching ? 'Switching…' : `Switch to ${confirmSwitch.label}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Re-verify confirmation ──────────────────────── */}
            {confirmReverify && current && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex items-center justify-between px-5 py-3 border-b">
                            <h3 className="font-semibold">
                                {confirmReverify === 'failed'
                                    ? `Try the ${failedCount} refused account(s) again under ${current.label}?`
                                    : `Re-verify ${needsReverify} bank account(s) under ${current.label}?`}
                            </h3>
                            <button onClick={() => setConfirmReverify(false)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
                        </div>
                        <div className="px-5 py-4 text-sm text-gray-700 space-y-2">
                            <p>
                                Each account is looked up with {current.label} and its bank code updated. Nothing is
                                sent — this only prepares the accounts.
                            </p>
                            <p className="text-gray-500 text-xs">
                                Accounts that cannot be matched are listed afterwards so those riders can be asked to
                                re-add their bank. It runs in batches; leave this page open until it finishes.
                            </p>
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-3 border-t">
                            <button onClick={() => setConfirmReverify(false)}
                                className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm">Cancel</button>
                            <button onClick={doReverify}
                                className="bg-amber-600 text-white px-4 py-2 rounded text-sm">Re-verify now</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PayoutProviderSwitch;
